<template>
  <v-card>
    <v-card-title :class="title.cssClass">
      <span class="headline">
        <v-icon :class="title.cssClass">{{title.icon}}</v-icon> {{title.title}} Conversion
      </span>
    </v-card-title>

    <v-card v-if="errors" class="red lighten-4">
      <v-card-title>
        <span class="headline">Errors</span>
      </v-card-title>
      <v-card-text>
        <p v-for="error in errors" v-html="error"></p>
      </v-card-text>
    </v-card>

    <v-stepper v-model="step" vertical>

      <!-- STEP 1: Enter and validate url -->
      <v-stepper-step
        step="1"
        v-bind:complete="step > 1"
      >
        Enter {{title.title}}
      </v-stepper-step>
      <v-stepper-content step="1">
        <v-form ref="URLInputForm">
          <v-text-field
            label="URL"
            class="input-group--focused"
            :rules="urlRules"
            :error-messages="urlErrors"
            v-model="url"
            v-on:keydown.enter.prevent="executeURLInputForm"
            required
          ></v-text-field>
        </v-form>
        <v-flex>
          <v-btn color="primary" @click="executeURLInputForm()">
            <v-progress-circular v-if="processing" indeterminate class="grey--text">Processing...</v-progress-circular>
            <span v-else>Continue</span>
          </v-btn>
        </v-flex>
      </v-stepper-content>

      <!-- STEP 2: select layer -->
      <v-stepper-step
        step="2"
        v-bind:complete="step > 2"
      >Select Layer</v-stepper-step>
      <v-stepper-content step="2">
        <span v-if="layers.length === 0">No layers found, please check your URL.</span>
        <v-form ref="LayerSelectForm">
          <v-select
            v-bind:items="layers"
            v-model="selectedLayer"
            item-text="name"
            item-key="name"
            label="Select Layer"
            :rules="layerRules"
            :error-messages="layersErrors"
            v-on:keydown.enter.prevent="executeLayerSelectForm"
            single-line
            required
          ></v-select>
        </v-form>
        <v-btn color="primary" @click.native="executeLayerSelectForm()">Continue</v-btn>
        <v-btn color="secondary" @click="back()">Back</v-btn>
        <v-btn color="error" @click="cancel()">Cancel</v-btn>
      </v-stepper-content>

      <!-- STEP 3: select output format -->
      <v-stepper-step
        step="3"
        v-bind:complete="step > 3"
      >Choose export format and Options</v-stepper-step>
      <v-stepper-content step="3">
        <v-form ref="ExportFormatSelectionForm">
          <v-select
            v-if="exportFormats"
            label="Select export format"
            hint="The format in which you want the input data to be converted to."
            persistent-hint
            :items="exportFormats"
            v-model="output_format"
            item-value="name"
            item-text="description"
            item-key="name"
            autocomplete
            :rules="exportFormatRules"
            :error-messages="output_formatErrors"
            v-on:keydown.enter.prevent="executeExportFormatSelectionForm"
            required
          ></v-select>

        </v-form>
        <v-btn color="primary" @click.native="convert()">Convert</v-btn>
        <v-btn color="secondary" @click="back()">Back</v-btn>
        <v-btn color="error" @click="cancel()">Cancel</v-btn>

        <v-card class="grey lighten-5 mt-3" tile>

          <v-card-title>
            <h6 class="header">Optional Parameters</h6>
          </v-card-title>

          <v-card tile flat class="blue-grey lighten-4 ma-0">

            <v-card-text class="ma-0 pt-0">

              <h6 class="subheader ma-0 pa-0">Bounding Box<v-bounding-box-usage-instructions /></h6>

              <v-text-field
                label="Bounding Box"
                hint="Use comma separated values defining a easting/northing (lat/lon), as for example delivered by http://boundingbox.klokantech.com/"
                v-model="bounding_box"
                :error-messages="bounding_boxErrors"
                persistent-hint
              ></v-text-field>

              <v-text-field
                label="Bounding Box CRS (EPSG code)"
                hint="For example `EPSG:4326` (without quotes)."
                v-model="bounding_box_crs"
                :error-messages="bounding_box_crsErrors"
                persistent-hint
              ></v-text-field>

            </v-card-text>
          </v-card>

          <v-card class="blue-grey lighten-5 ma-0" tile>
            <v-card-text class="ma-0 pt-0">
              <h6 class="subheader ma-0 pa-0">Input/Output Parameters</h6>

              <v-form ref="OptionsForm">
                <v-text-field
                  label="Source CRS (EPSG code)"
                  hint="Only needed when it can not be determined from input. For example `EPSG:4326` (without quotes)."
                  v-model="input_crs"
                  :error-messages="input_crsErrors"
                  persistent-hint
                ></v-text-field>
                <v-text-field
                  label="Output CRS (EPSG code)"
                  hint="For example `EPSG:4326` (without quotes)."
                  v-model="output_crs"
                  :error-messages="output_crsErrors"
                  persistent-hint
                ></v-text-field>
                <v-text-field
                  v-if="conversionType === 'WMS'"
                  label="Output Image width"
                  hint="Image width in pixels, height will be selected automatically"
                  v-model="output_image_width"
                  :error-messages="output_image_widthErrors"
                  persistent-hint
                ></v-text-field>
              </v-form>
            </v-card-text>
          </v-card>
        </v-card>

      </v-stepper-content>

      <!-- STEP 4: Enter and validate url -->
      <v-stepper-step
        step="4"
        v-bind:complete="step > 4"
      >
        Submitted.
      </v-stepper-step>
      <v-stepper-content step="4">
        <p>
        Please collect your result below. This might take a while, depending on the velocity of the service
        and the size of the data.
        </p>
        <v-btn color="primary" @click="resetForms();resetErrors();step=1">Start a new conversion.</v-btn>
      </v-stepper-content>
    </v-stepper>

  </v-card>
</template>

<script>
  import validator from 'validator';
  import _ from 'lodash';
  import Raven from 'raven-js';

  import GeoResults from '@/components/converter/output/ConversionResults';
  import BoundingBoxUsageInstructions from '@/components/converter/input/BoundingBoxUsageInstructions';
  import { getLayers, getFormats, convert } from '@/components/helpers/API';
  import geoTypes from '@/components/converter/helpers/geoTypes';

  export default {
    name: 'geo-converter',
    props: {
      conversionType: {
        required: true,
        type: String,
      },
    },
    data() {
      return {
        url: null,
        // url: 'http://wms.zh.ch/TBAAnlagenZHWMS',
        // url: 'http://maps.zh.ch/wfs/TBA9ZHWFS',
        // url: 'http://wms.zh.ch/TBAAnlagenZHWMS?LAYERS=TBAAnlagenZHWMS&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng%3B%20mode%3D8bit&CRS=EPSG%3A21781&BBOX=680000,243000,696931,255698&WIDTH=800&HEIGHT=600',
        selectedLayer: null,
        output_format: null,
        input_crs: null,
        output_crs: null,
        bounding_box: null,
        bounding_box_crs: 'EPSG:4326',
        output_image_width: null,
        layers: [],
        exportFormats: [],
        errors: null,
        allErrors: null,
        urlErrors: [],
        layersErrors: [],
        input_crsErrors: [],
        bounding_boxErrors: [],
        bounding_box_crsErrors: [],
        output_formatErrors: [],
        output_crsErrors: [],
        output_image_widthErrors: [],
        step: 1,
        processing: false,
      };
    },
    created() {
      getFormats(this.conversionType)
        .then((exportFormats) => {
          this.exportFormats = exportFormats;
        })
        .catch((errors) => {
          Raven.captureException(errors);
        });
    },
    components: {
      'conversion-results': GeoResults,
      'v-bounding-box-usage-instructions': BoundingBoxUsageInstructions,
    },
    methods: {
      executeURLInputForm() {
        if (this.isURLInputFormValid) {
          this.processing = true;

          const params = {
            url: this.url,
            conversion_type: this.conversionType,
          };
          this.resetErrors();

          getLayers(params)
            .then((layers) => {
              this.layers = layers;
              if (layers.length === 1) {
                [this.selectedLayer] = layers;
              }
              this.step = 2;
              this.processing = false;
            })
            .catch(this.handleErrors);
        }
      },
      executeLayerSelectForm() {
        if (this.isLayerFormValid) {
          this.step = 3;
        }
      },
      convert() {
        if (this.isExportFormatSelectionValid) {
          if (this.isOptionsFormValid) {
            const url = this.selectedLayer.url || this.url;
            const postData = {
              url,
              layer: this.selectedLayer.name,
              conversion_type: this.conversionType,
              input_crs: this.input_crs,
              output_crs: this.output_crs,
              output_format: this.output_format,
              output_image_width: this.output_image_width,
              bounding_box: this.boundingBoxAsArray,
              bounding_box_crs: this.bounding_box ? this.bounding_box_crs : null,
            };
            this.resetErrors();
            this.processing = true;

            convert(postData)
              .then((conversion) => {
                this.step = 4;
                this.$store.commit('addConversion', conversion);
                this.resetForms();
                this.processing = false;
              })
              .catch(this.handleErrors);
          }
        }
      },
      back() {
        this.step -= 1;
      },
      cancel() {
        this.step = 1;
        this.resetErrors();
      },
      resetForms() {
        this.selectedLayer = null;
        this.output_format = null;
        this.bounding_box = null;
        this.bounding_box_crs = 'EPSG:4326';
        this.input_crs = null;
        this.output_crs = null;
        this.output_image_width = null;
        this.layers = [];
        this.resetErrors();
      },
      resetErrors() {
        this.errors = null;
        this.allErrors = null;
        this.urlErrors = [];
        this.layersErrors = [];
        this.input_crsErrors = [];
        this.bounding_boxErrors = [];
        this.bounding_box_crsErrors = [];
        this.output_formatErrors = [];
        this.output_crsErrors = [];
        this.output_image_widthErrors = [];
      },
      handleErrors(errs) {
        this.allErrors = errs;
        const errors = Object.assign({}, errs);
        this.processing = false;

        const fieldToErrorsName = {
          url: 'urlErrors',
          layers: 'layersErrors',
          input_crs: 'input_crsErrors',
          output_format: 'output_formatErrors',
          output_crs: 'output_crsErrors',
          output_image_width: 'output_image_widthErrors',
        };
        if (!_.isEmpty(errors)) {
          Object.keys(fieldToErrorsName).forEach((fieldName) => {
            this[fieldToErrorsName[fieldName]] = errors[fieldName];
            delete errors[fieldName];
          });
        }
        if (!_.isEmpty(errors)) {
          this.errors = errors;
        }
      },
    },
    computed: {
      title() {
        const conversionType = this.conversionType.toLowerCase();
        return geoTypes[conversionType];
      },
      urlRules() {
        return [
          v => !!v || 'URL required',
          v => validator.isURL(v) || 'Invalid URL',
        ];
      },
      layerRules() {
        return [
          v => !!v || 'Layer selection required',
        ];
      },
      exportFormatRules() {
        return [
          v => !!v || 'Export format selection required',
        ];
      },
      isURLInputFormValid() {
        return this.$refs.URLInputForm.validate();
      },
      isLayerFormValid() {
        return this.$refs.LayerSelectForm.validate();
      },
      isExportFormatSelectionValid() {
        return this.$refs.ExportFormatSelectionForm.validate();
      },
      isOptionsFormValid() {
        return this.$refs.OptionsForm.validate();
      },
      boundingBoxAsArray() {
        if (this.bounding_box) {
          if (Array.isArray(this.bounding_box)) {
            return this.bounding_box;
          }
          return this.bounding_box.split(',');
        }
        return null;
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/converter/input/GeoConverterURL.vue